import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import layoutConf from "../../config/layoutConf";

import getSchema from "../../lib/api/getSchema";
import notificationState from "../../states/notificationState";
import AccessControl from "../functional/AccessControl";

import TkPopNoticeDisplay from "./TkPopNoticeDisplay";
import TkPopNoticeCreateButton from "../input/TkPopNoticeCreateButton";
import TkPopNoticeUpdateButton from "../input/TkPopNoticeUpdateButton";
import TkPopNoticeDeleteButton from "../input/TkPopNoticeDeleteButton";

import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import ToolbarFlexSpacer from "../layout/ToolbarFlexSpacer";
import BasicCrudActionsLayout from "../screenTypes/layout/BasicCrudActionsLayout";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";

function MomSysNotice(props) {
    const id = "TkPopNotice";
    const table = "TkPopNotice";
    const view = "TkVwPopNotice";

    const [tkPopNoticeSchema, setTkPopNoticeSchema] = useState();
    const [tkPopNoticeGrid, setTkPopNoticeGrid] = useState();
    const [tkPopNoticeApi, setTkPopNoticeApi] = useState();
    const [tkPopNoticeDataIsEmpty, setTkPopNoticeDataIsEmpty] = useState();
    const [tkPopNoticeSelect, setTkPopNoticeSelect] = useState();

    const [tkPopNoticeContent, setTkPopNoticeContent] = useState();
    const [tkPopNoticeRefreshToggle, setTkPopNoticeRefreshToggle] = useState(false);

    const [height, setHeight] = useState();

    const { t } = useTranslation();

    const [, setNotification] = useRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();

        let mounted = true;

        (async () => {
            let schema = await getSchema(table);
            if (mounted) {
                setTkPopNoticeSchema(schema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (tkPopNoticeGrid) {
                tkPopNoticeGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkPopNoticeSelect(grid.selectedItems[0]);
                        }
                    }),
                    10
                );

                tkPopNoticeGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkPopNoticeSelect(grid.selectedItems[0]);
                        }
                    }),
                    10
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tkPopNoticeGrid]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });

        if (tkPopNoticeApi) {
            tkPopNoticeApi.refresh();
        }
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "failure",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.5}>
                    <BasicCrudScreenType
                        id={id}
                        table={table}
                        view={view}
                        onInitialized={(grid) => {
                            grid.selectionMode = "Row";
                            setTkPopNoticeGrid(grid);
                        }}
                        onApiInitialized={(api) => {
                            setTkPopNoticeApi(api);
                        }}
                        onLoadData={(data) => {
                            setTkPopNoticeDataIsEmpty(data.length < 1 ? true : false);
                        }}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement>
                    {tkPopNoticeSelect ? (
                        <>
                            <BasicCrudActionsLayout className="basicCrudScreenActionsLayout">
                                {/*<ToolbarFlexSpacer xs />*/}
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl
                                        accessTarget={tkPopNoticeSchema && tkPopNoticeSchema.name}
                                        accessMethod="POST"
                                    >
                                        <TkPopNoticeCreateButton
                                            id={id}
                                            schema={tkPopNoticeSchema}
                                            onSetRefreshToggle={() => {
                                                setTkPopNoticeRefreshToggle((refreshToggle) => !refreshToggle);
                                            }}
                                            onSelectMst={tkPopNoticeSelect}
                                            disabled={tkPopNoticeContent}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={tkPopNoticeSchema && tkPopNoticeSchema.name}
                                        accessMethod={
                                            tkPopNoticeSchema && (tkPopNoticeSchema.updateWithPatch ? "PATCH" : "PUT")
                                        }
                                    >
                                        <TkPopNoticeUpdateButton
                                            id={id}
                                            schema={tkPopNoticeSchema}
                                            onSetRefreshToggle={() => {
                                                setTkPopNoticeRefreshToggle((refreshToggle) => !refreshToggle);
                                            }}
                                            onSelectMst={tkPopNoticeSelect}
                                            disabled={!tkPopNoticeContent}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={tkPopNoticeSchema && tkPopNoticeSchema.name}
                                        accessMethod="DELETE"
                                    >
                                        <TkPopNoticeDeleteButton
                                            id={id}
                                            schema={tkPopNoticeSchema}
                                            makeItemName={(row) => {
                                                return ` - ${t("columnName.workOrderNumber")}: ${
                                                    row.workOrderNumber
                                                }, ${t("columnName.partNo")}: ${row.partNo}`;
                                            }}
                                            onSetRefreshToggle={() => {
                                                setTkPopNoticeRefreshToggle((refreshToggle) => !refreshToggle);
                                            }}
                                            onSelectMst={tkPopNoticeSelect}
                                            disabled={!tkPopNoticeContent}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                            </BasicCrudActionsLayout>
                            <TkPopNoticeDisplay
                                workOrderNumber={tkPopNoticeSelect.workOrderNumber}
                                partNo={tkPopNoticeSelect.partNo}
                                onSetContent={setTkPopNoticeContent}
                                refreshToggle={tkPopNoticeRefreshToggle}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default MomSysNotice;
